// src/pages/ESG/Report/ReportResult.jsx
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import useScrollAnimation from '../../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../../components/common/AnimatedSection';

/* ====== layout ====== */
const PageContainer = styled.div`
  min-height: 100vh;
  background: #fff;
  padding-top: 10%;
  padding-bottom: 8rem;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

/* ====== header/meta ====== */
const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 3rem 0;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111111;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Meta = styled.div`
  color: #6b7280;
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
`;

/* ====== stepper ====== */
const StepperWrap = styled.div`
  position: relative;
  padding: 18px 16px;
  margin-bottom: 20px;
`;
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  position: relative;
  margin: 0;
  padding: 4px 8px 0;
  list-style: none;

  /* connectors */
  &::before {
    content: '';
    position: absolute;
    top: 18px;
    left: -5px;
    right: -5px;
    height: 6px;
    background: #e5e7eb;
    border-radius: 999px;
    z-index: 1; 
  }
`;
const Step = styled.li`
  position: relative;
  text-align: center;
  z-index: 3;
`;
const Dot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  border: 3px solid currentColor;
  color: ${({ state }) =>
    state === 'active' ? '#1d4ed8' : state === 'done' ? '#16a34a' : '#9ca3af'};
  background: ${({ state }) =>
    state === 'active' ? '#dbeafe' : state === 'done' ? '#dcfce7' : '#fff'};
  box-shadow: ${({ state }) =>
    state === 'active' ? '0 0 0 3px rgba(29,78,216,.12)' : 'none'};
`;
const Label = styled.div`
  margin-top: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ state }) =>
    state === 'active' ? '#1f2937' : state === 'done' ? '#065f46' : '#6b7280'};
`;
const When = styled.div`
  margin-top: 6px;
  font-size: 0.82rem;
  color: #6b7280;
`;

/* “채워지는” 진행 바 (완료/활성 전까지) */
const ProgressBar = styled.div`
  position: absolute;
  top: 36px;
  left: 12px;
  height: 6px;
  background: #3b82f6;
  border-radius: 999px;
  width: ${({ idx }) => (idx === 0 ? '18%' : idx === 1 ? '50%' : '100%')};
  transition: width 300ms ease;
  z-index: 2; 
`;

/* ====== info card ====== */
const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  background: #fafafa;
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px dashed #e5e7eb;
  &:first-child { border-top: 0; }
`;
const Badge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${p => p.color || '#1f2937'};
  background: ${p => p.bg || '#e5e7eb'};
`;
const NoteBox = styled.pre`
  white-space: pre-wrap;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin: 0;
`;

/* ====== Back button (디자인 개선) ====== */
const BackBar = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-start;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 9999px;
  background: #ffffff;
  color: #a1a3a4ff;
  font-weight: 500;
  text-decoration: none;
  transition: background .2s ease, box-shadow .2s ease, transform .06s ease;

  &:hover {
    color: #121212;
    font-weight: 600;
  }
  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(59,130,246,.18);
  }

  /* 왼쪽 화살표 느낌 */
  &::before {
    content: '〈';
    opacity: .85;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

/* ====== utils ====== */
const formatDT = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return '—'; }
};
const humanSize = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

export default function ReportResult() {
  const { referenceId } = useParams();
  const [headerRef, headerVisible] = useScrollAnimation();
  const [ref2, vis2] = useScrollAnimation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const API = process.env.REACT_APP_API_BASE || 'http://localhost:8080';

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/reports/status/${encodeURIComponent(referenceId)}`);
        if (!res.ok) throw new Error(await res.text());
        const j = await res.json();
        if (alive) setData(j);
      } catch (e) {
        console.error(e);
        setErr('해당 참조번호의 정보를 조회할 수 없습니다.');
      }
    })();
    return () => { alive = false; };
  }, [API, referenceId]);

  const statusToIdx = (s) =>
    s === 'RECEIVED' ? 0 : s === 'IN_PROGRESS' ? 1 : s === 'CLOSED' ? 2 : 0;

  const statusStyle = (s) => {
    switch (s) {
      case 'RECEIVED':    return { bg:'#DBEAFE', color:'#1D4ED8' };
      case 'IN_PROGRESS': return { bg:'#FEF3C7', color:'#B45309' };
      case 'CLOSED':      return { bg:'#DCFCE7', color:'#166534' };
      default:            return { bg:'#E5E7EB', color:'#374151' };
    }
  };

  // 타임스탬프 추정/표시 로직
  const submittedAt   = data?.createdAt ?? null;
  const inProgressAt  = data?.inProgressAt || data?.progressStartedAt ||
                        (data?.status !== 'RECEIVED' ? data?.updatedAt : null) || null;
  const closedAt      = data?.closedAt || (data?.status === 'CLOSED' ? data?.updatedAt : null) || null;

  const activeIdx = statusToIdx(data?.status);

  return (
    <PageContainer>
      <Container>
        <AnimatedSection ref={headerRef} isVisible={headerVisible} animation="fadeInUp">
          <PageHeader>
            <PageTitle>제보 진행 현황</PageTitle>
          </PageHeader>
        </AnimatedSection>

        <AnimatedSection ref={ref2} isVisible={vis2} animation="fadeInUp" delay="0.05s">
          {!data && !err && <div>불러오는 중...</div>}
          {err && <div style={{ color: '#b91c1c' }}>{err}</div>}

          {data && (
            <>
              {/* ====== 3-step progress ====== */}
              <StepperWrap>
                <ProgressBar idx={activeIdx} />
                <Steps>
                  <Step>
                    <Dot state={activeIdx === 0 ? 'active' : activeIdx > 0 ? 'done' : 'todo'}>1</Dot>
                    <Label state={activeIdx === 0 ? 'active' : activeIdx > 0 ? 'done' : 'todo'}>
                      접수 완료
                    </Label>
                    <When>{formatDT(submittedAt)}</When>
                  </Step>

                  <Step>
                    <Dot state={activeIdx === 1 ? 'active' : activeIdx > 1 ? 'done' : 'todo'}>2</Dot>
                    <Label state={activeIdx === 1 ? 'active' : activeIdx > 1 ? 'done' : 'todo'}>
                      진행 중
                    </Label>
                    <When>{formatDT(inProgressAt)}</When>
                  </Step>

                  <Step>
                    <Dot state={activeIdx === 2 ? 'active' : activeIdx > 2 ? 'done' : 'todo'}>3</Dot>
                    <Label state={activeIdx === 2 ? 'active' : activeIdx > 2 ? 'done' : 'todo'}>
                      종결
                    </Label>
                    <When>{formatDT(closedAt)}</When>
                  </Step>
                </Steps>
              </StepperWrap>

              {/* ====== details ====== */}
              <Card>
                {/* 기본/대상 정보 */}
                <Row><div>제목</div><div>{data.title || '-'}</div></Row>
                <Row><div>제보대상 유형</div><div>{data.targetType || '-'}</div></Row>
                <Row><div>제보 대상자</div><div>{data.targetName || '-'}</div></Row>
                <Row><div>부서/업무</div><div>{data.targetDept || '-'}</div></Row>
                <Row><div>사건 주요내용</div><div>{data.incidentSummary || '-'}</div></Row>

                {/* 본문/관계/첨부/조치 */}
                <Row>
                  <div>내용</div>
                  <div><NoteBox>{data.content || '-'}</NoteBox></div>
                </Row>
                <Row><div>소속회사와의 관계</div><div>{data.relation || '-'}</div></Row>
                <Row>
                  <div>첨부파일</div>
                  <div>
                    {data.attachmentName
                      ? `${data.attachmentName}${data.attachmentSize ? ` (${humanSize(data.attachmentSize)})` : ''}`
                      : '등록된 첨부 없음'}
                  </div>
                </Row>
                <Row>
                  <div>조치/답변</div>
                  <div><NoteBox>{data.actionNotes || '등록된 조치 내용이 없습니다.'}</NoteBox></div>
                </Row>
              </Card>

              {/* 다시 조회하기 - 디자인 개선 */}
              <BackBar>
                <BackButton to="/report/check">다시 조회하기</BackButton>
              </BackBar>
            </>
          )}
        </AnimatedSection>
      </Container>
    </PageContainer>
  );
}
